import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { EyebrowHeading, Section } from "@/components/site/Section";
import { ArticleCard } from "@/components/site/ArticleCard";
import { listBlogPosts } from "@/lib/blog.functions";
import { ContactForm } from "@/components/site/ContactForm";
import { LeadMagnetForm } from "@/components/site/LeadMagnetForm";
import { EditorialHero } from "@/components/site/wow/EditorialHero";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { aeoGeo, breadcrumbs } from "@/lib/seo/schema";

export const Route = createFileRoute("/kunskap")({
  head: () => ({
    meta: [
      { title: "Kunskap: guider om film och rörligt innehåll | Fenny" },
      {
        name: "description",
        content:
          "Strategier, format och insikter för marknadsteam som jobbar med rörligt innehåll. Skrivet av Fenny — premiumfilm som abonnemang.",
      },
      { property: "og:title", content: "Kunskap: guider om film och rörligt innehåll | Fenny" },
      {
        property: "og:description",
        content:
          "Strategier, format och insikter för marknadsteam som jobbar med rörligt innehåll.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/kunskap" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/kunskap" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/kunskap", name: "Kunskap — Fennys blogg om rörligt innehåll", description: "Strategier, format och insikter för marknadsteam som jobbar med rörligt innehåll. Skrivet av Fenny — premiumfilm som abonnemang."})) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbs([{ name: "Hem", path: "/" }, { name: "Kunskap", path: "/kunskap" }])) },
    ],
  }),
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: ["blog-posts"],
      queryFn: () => listBlogPosts(),
    }),
  component: KunskapPage,
});

function KunskapPage() {
  const fetchPosts = useServerFn(listBlogPosts);
  const { data } = useQuery({
    queryKey: ["blog-posts"],
    queryFn: () => fetchPosts(),
  });
  const posts = data?.posts ?? [];
  const [featured, ...rest] = posts;

  return (
    <div>
      <EditorialHero
        eyebrow="Kunskap"
        title="Vi skriver om det vi gör."
        highlight={[3, 4, 5]}
        tone="mint"
        lede="Strategi, kreativ riktning, format, distribution och allt däremellan. Det vi lär oss av varje produktion samlat på ett ställe — utan buzzwords."
      />

      {featured && (
        <Section className="pt-4">
          <ArticleCard post={featured} featured />
        </Section>
      )}

      <Section className="bg-surface/40">
        <EyebrowHeading
          eyebrow="Senaste"
          title="Fler från biblioteket"
          lede="Korta, konkreta artiklar baserade på riktiga produktioner och kampanjer."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((p) => (
            <ArticleCard key={p.slug} post={p} />
          ))}
        </div>
        {posts.length === 0 && (
          <p className="mt-12 text-center text-muted-foreground">
            Inga publicerade artiklar än.
          </p>
        )}
      </Section>

      <Section className="bg-surface/40">
        <div className="mx-auto max-w-4xl">
          <LeadMagnetForm id="formatguide" location="kunskap_lead_magnet" />
        </div>
      </Section>

      <SignatureCta
        eyebrow="Från kunskap till handling"
        title="Det här blir filmer. Vi gör dem."
        body="Allt vi skriver om utförs varje vecka för våra Pulse-kunder. 30 minuters samtal räcker för att se om det är något för er."
        tone="mint"
      />

      <ContactForm location="kunskap" />
    </div>
  );
}
